import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { LayoutDashboard, FileText, CreditCard, Plus, Trash2, Layers, BookOpen, Pencil, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { key: "home", label: "Overview", to: "/admin", icon: LayoutDashboard },
  { key: "users", label: "Users & Papers", to: "/admin/users", icon: Users },
  { key: "plans", label: "Purchase Plans", to: "/admin/plans", icon: CreditCard },
  { key: "papers", label: "Question Papers", to: "/admin/papers", icon: FileText },
  { key: "failed", label: "Failed evaluations", to: "/admin/failed-evaluations", icon: AlertTriangle },
];

const DEFAULT_SUBJECTS = ["Mathematics", "Science", "English", "History", "Social Science", "Hindi"];
const LANGS = ["English", "Hindi", "Tamil", "Telugu", "Kannada", "Malayalam", "Marathi", "Bengali", "Gujarati", "Punjabi", "Urdu", "Odia"];

const derivePlanType = (subjects, sets) => {
  const ms = subjects.length > 1;
  const mset = sets > 1;
  if (!ms && !mset) return "1 Subject · Single Set";
  if (!ms && mset) return "1 Subject · Multiple Sets";
  if (ms && !mset) return "Multiple Subjects · Single Set";
  return "Multiple Subjects · Multiple Sets";
};

export default function PlansAdmin() {
  const [plans, setPlans] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState(DEFAULT_SUBJECTS);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", price_inr: 199, subjects: [], sets: 1, medium: "English", customSubject: "", features: "" });

  const load = () => api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/subjects").then((r) => {
      const merged = Array.from(new Set([...DEFAULT_SUBJECTS, ...r.data]));
      setSubjectOptions(merged);
    }).catch(() => {});
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({ name: "", price_inr: 199, subjects: [], sets: 1, medium: "English", customSubject: "", features: "" });
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setSubjectOptions((o) => Array.from(new Set([...o, ...(p.subjects || [])])));
    setForm({ name: p.name, price_inr: p.price_inr, subjects: p.subjects || [], sets: p.sets || 1, medium: p.medium || "English", customSubject: "", features: (p.features || []).join(", ") });
    setOpen(true);
  };

  const toggleSubject = (s) => {
    setForm((f) => ({ ...f, subjects: f.subjects.includes(s) ? f.subjects.filter((x) => x !== s) : [...f.subjects, s] }));
  };

  const addCustom = () => {
    const s = form.customSubject.trim();
    if (!s) return;
    setSubjectOptions((o) => Array.from(new Set([...o, s])));
    setForm((f) => ({ ...f, subjects: Array.from(new Set([...f.subjects, s])), customSubject: "" }));
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Enter a plan name");
    if (form.subjects.length === 0) return toast.error("Select at least one subject");
    setSaving(true);
    const payload = {
      name: form.name,
      price_inr: Number(form.price_inr),
      subjects: form.subjects,
      sets: Number(form.sets),
      medium: form.medium || "English",
      features: form.features.split(",").map((x) => x.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await api.put(`/plans/${editingId}`, payload);
        toast.success("Plan updated");
      } else {
        await api.post("/plans", payload);
        toast.success("Plan created");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/plans/${id}`);
      toast.success("Plan deactivated");
      load();
    } catch {
      toast.error("Failed to deactivate");
    }
  };

  return (
    <DashboardShell nav={NAV} title="Purchase Plans">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">Create plans across the four types: single/multiple subjects × single/multiple sets. The plan type is derived automatically.</p>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <Button className="rounded-full gap-2" data-testid="new-plan-button" onClick={openCreate}><Plus className="h-4 w-4" /> New Plan</Button>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Purchase Plan" : "Create Purchase Plan"}</DialogTitle>
              <DialogDescription>Type: <b>{derivePlanType(form.subjects, Number(form.sets))}</b></DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Plan name</Label><Input data-testid="plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-2"><Label>Price (₹)</Label><Input type="number" data-testid="plan-price" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: e.target.value })} /></div>
              </div>

              <div className="space-y-2">
                <Label>Subjects</Label>
                <div className="grid grid-cols-2 gap-2 border border-border rounded-md p-3">
                  {subjectOptions.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`subject-option-${s}`}>
                      <Checkbox checked={form.subjects.includes(s)} onCheckedChange={() => toggleSubject(s)} />
                      {s}
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input placeholder="Add custom subject" data-testid="custom-subject-input" value={form.customSubject} onChange={(e) => setForm({ ...form, customSubject: e.target.value })} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())} />
                  <Button type="button" variant="outline" onClick={addCustom}>Add</Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Sets per subject</Label>
                <Input type="number" min={1} data-testid="plan-sets" value={form.sets} onChange={(e) => setForm({ ...form, sets: e.target.value })} />
                <p className="text-xs text-muted-foreground">Total evaluations = {form.subjects.length || 0} subject(s) × {form.sets} set(s) = <b>{(form.subjects.length || 0) * Number(form.sets || 0)}</b></p>
              </div>

              <div className="space-y-2"><Label>Features (comma separated)</Label><Input data-testid="plan-features" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder="AI evaluation, PDF report" /></div>

              <div className="space-y-2">
                <Label>Medium of instruction (feedback language)</Label>
                <Select value={form.medium} onValueChange={(v) => setForm({ ...form, medium: v })}>
                  <SelectTrigger data-testid="plan-medium"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Used as the feedback language when a question paper does not specify one.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="save-plan-button">{saving ? "Saving…" : editingId ? "Save changes" : "Create plan"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.length === 0 && <p className="text-sm text-muted-foreground">No plans yet. Create one.</p>}
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-admin-${p.id}`} className="bg-background border border-border rounded-lg p-5 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200">
            <div className="flex items-start justify-between">
              <Badge variant="outline" className="mb-2">{p.plan_type_label}</Badge>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(p)} className="text-muted-foreground hover:text-primary" data-testid={`edit-plan-${p.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(p.id)} className="text-destructive" data-testid={`delete-plan-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <h3 className="font-heading font-bold text-lg">{p.name}</h3>
            <p className="font-heading font-black text-3xl mt-2">₹{p.price_inr}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(p.subjects || []).map((s) => (
                <span key={s} className="inline-flex items-center gap-1 text-xs bg-secondary rounded-full px-2 py-0.5"><BookOpen className="h-3 w-3" />{s}</span>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-3 flex items-center gap-2"><Layers className="h-4 w-4" /> {p.sets} set(s) · {p.scripts_quota} evaluations</p>
            <p className="text-xs text-muted-foreground mt-1" data-testid={`plan-medium-${p.id}`}>Medium: <b>{p.medium || "English"}</b></p>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}
